/**
 * PHI scrubbing for Sentry events and breadcrumbs.
 *
 * Sentry runs in production and must never receive Protected Health Information.
 * This module is pure (no Sentry import) so it is unit-testable in isolation and
 * wired into `Sentry.init` via `beforeSend` / `beforeBreadcrumb`.
 *
 * Strategy (defense in depth):
 *   1. Drop request/response bodies, cookies, and headers — these are the most
 *      likely carriers of clinical text or LLM prompts.
 *   2. Reduce `user` to an opaque id only (no email/username/ip/geo).
 *   3. `extra` is fully app-controlled free-form data, so it is ALLOWLISTED:
 *      only known-safe keys survive. An allowlist (vs a denylist) means a field
 *      added later is redacted by default instead of leaking silently.
 *   4. Everything else (contexts, tags, breadcrumb data) is deep-scrubbed by
 *      redacting any property whose KEY looks PHI-shaped.
 *   5. `console` breadcrumbs are dropped entirely — console logs frequently
 *      contain raw clinical objects or prompts.
 */

export const REDACTED = "[Redacted]";

/**
 * Keys permitted to survive inside `event.extra`. Deliberately small and
 * non-clinical. Anything not listed here is dropped.
 */
export const EXTRA_ALLOWLIST = new Set([
  "component",
  "feature",
  "route",
  "path",
  "method",
  "statusCode",
  "status_code",
  "code",
  "count",
  "duration",
  "durationMs",
  "environment",
  "release",
  "version",
  "buildId",
  "locale",
]);

/**
 * Matches property names that may carry PHI or otherwise sensitive content.
 * Used for deep redaction of free-form containers.
 */
const PHI_KEY_PATTERN =
  /(name|patient|dob|birth|ssn|mrn|address|phone|email|zip|postal|transcript|medication|allerg|diagnos|symptom|complaint|prompt|completion|message|content|note|token|secret|password|apikey|api_key|authorization)/i;

/**
 * @param {string} key
 * @returns {boolean} true when the key name looks PHI-shaped / sensitive.
 */
export function isPhiKey(key) {
  return typeof key === "string" && PHI_KEY_PATTERN.test(key);
}

/**
 * Redact a free-text string (exception message, `event.message`) that shows any
 * PHI-shaped indicator. Unlike keyed containers, the actual PHI here (e.g. a
 * patient name or DOB) is unstructured, so a partial word-level redaction can't
 * reliably remove it — if a PHI indicator is present we redact the WHOLE string.
 * Non-matching strings are returned unchanged so ordinary error text still
 * reaches Sentry for debugging.
 * @param {*} value
 * @returns {*}
 */
export function scrubText(value) {
  if (typeof value !== "string") return value;
  return PHI_KEY_PATTERN.test(value) ? REDACTED : value;
}

/**
 * Recursively redact any property whose key looks PHI-shaped. Non-matching
 * values are preserved (and objects/arrays walked). Guards against cycles and
 * pathological depth.
 * @param {*} value
 * @param {number} [depth]
 * @param {WeakSet} [seen]
 * @returns {*}
 */
export function deepScrub(value, depth = 0, seen = new WeakSet()) {
  if (depth > 8 || value == null) return value;
  if (Array.isArray(value)) {
    return value.map((item) => deepScrub(item, depth + 1, seen));
  }
  if (typeof value === "object") {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = isPhiKey(key) ? REDACTED : deepScrub(val, depth + 1, seen);
    }
    return out;
  }
  return value;
}

/**
 * Reduce `event.extra` to allowlisted, deep-scrubbed values only.
 * @param {object|null|undefined} extra
 * @returns {object|undefined}
 */
function scrubExtra(extra) {
  if (!extra || typeof extra !== "object") return extra || undefined;
  const out = {};
  for (const [key, val] of Object.entries(extra)) {
    if (EXTRA_ALLOWLIST.has(key) && !isPhiKey(key)) {
      out[key] = deepScrub(val);
    }
  }
  return out;
}

/**
 * Strip request bodies, cookies, headers, and query strings; keep only the
 * method and the path portion of the URL.
 * @param {object|null|undefined} request
 * @returns {object|undefined}
 */
function scrubRequest(request) {
  if (!request || typeof request !== "object") return request || undefined;
  const cleaned = {};
  if (typeof request.method === "string") cleaned.method = request.method;
  if (typeof request.url === "string") {
    // Drop the query string (may embed identifiers).
    cleaned.url = request.url.split("?")[0];
  }
  return cleaned;
}

/**
 * Redact PHI-shaped free text inside an exception container. Sentry stores
 * exceptions as `{ values: [{ type, value, stacktrace }] }`. The `value` is the
 * thrown message (most likely to carry interpolated clinical text); `type` (the
 * error class, e.g. "TypeError") is kept for grouping.
 * @param {object|null|undefined} exception
 * @returns {object|null|undefined}
 */
function scrubException(exception) {
  if (!exception || typeof exception !== "object") return exception;
  if (Array.isArray(exception.values)) {
    exception.values = exception.values.map((entry) => {
      if (entry && typeof entry === "object") {
        entry.value = scrubText(entry.value);
      }
      return entry;
    });
  }
  return exception;
}

/**
 * Scrub a full Sentry event in place-safe fashion (returns the same object).
 * Safe to pass directly as `beforeSend`.
 * @param {object|null|undefined} event
 * @returns {object|null|undefined}
 */
export function scrubEvent(event) {
  if (!event || typeof event !== "object") return event;

  if (event.request) event.request = scrubRequest(event.request);

  // Free-text fields that can carry interpolated PHI (e.g. a thrown
  // `Error(`failed for patient ${name}`)`). Structured fields are handled
  // below; these are scrubbed as whole strings.
  if (event.message) event.message = scrubText(event.message);
  if (event.exception) event.exception = scrubException(event.exception);

  if (event.user) {
    // Keep an opaque id for correlation; drop all other user PII.
    event.user = event.user.id ? { id: event.user.id } : {};
  }

  event.extra = scrubExtra(event.extra);
  if (event.contexts) event.contexts = deepScrub(event.contexts);
  if (event.tags) event.tags = deepScrub(event.tags);

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs
      .map(scrubBreadcrumb)
      .filter(Boolean);
  } else if (event.breadcrumbs && Array.isArray(event.breadcrumbs.values)) {
    event.breadcrumbs.values = event.breadcrumbs.values
      .map(scrubBreadcrumb)
      .filter(Boolean);
  }

  return event;
}

/**
 * Scrub a single breadcrumb. Console breadcrumbs are dropped entirely (they
 * commonly carry raw clinical objects/prompts); others have their `data`
 * deep-scrubbed. Return null to drop.
 * @param {object|null|undefined} breadcrumb
 * @returns {object|null}
 */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || typeof breadcrumb !== "object") return breadcrumb || null;
  if (breadcrumb.category === "console") return null;
  if (breadcrumb.data) breadcrumb.data = deepScrub(breadcrumb.data);
  return breadcrumb;
}
